// This client supports two lifecycle-specific requests: boardableToday limits a
// patient lookup to today's detached Scheduled plan, and startBookingSession
// moves a visit-linked booking from Boarded/Scheduled to In Progress.
import { helperServices } from "../../shared/helper";
import { apiFetch } from "../../shared/helper/apiFetch";

const basePath = "/api";

// ── Product search ─────────────────────────────────────────
export async function searchProducts(searchTerm) {
  return apiFetch(`${basePath}/v1/searchProduct?searchvalue=${encodeURIComponent(searchTerm)}&location=0`, {
    method: "POST", headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: helperServices.getAuthorization() || "",
    },
  });
}

function buildHeaders() {
  const accessToken = helperServices.getAuthorization() || "";
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: accessToken,
  };
}

// ── Universal list extractor ──────────────────────────────
function extractList(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data))  return response.data.data;
  if (Array.isArray(response?.data))        return response.data;

  const knownFields = [
    'personList',
    'roomList',
    'packageList',
    'serviceList',
    'packageServiceList',
    'patientList',
    'patient',
    'patsearchreslt',
    'procedureList',
    'list',
    'items',
    'results',
  ];

  for (const field of knownFields) {
    if (Array.isArray(response?.[field])) return response[field];
  }

  if (Array.isArray(response?.accresp?.personList)) return response.accresp.personList;

  if (typeof response === 'object') {
    for (const key of Object.keys(response)) {
      if (Array.isArray(response[key]) && response[key].length > 0) {
        console.log(`[bookingPlanService] extractList: found data under key "${key}"`);
        return response[key];
      }
    }
    for (const key of Object.keys(response)) {
      if (Array.isArray(response[key])) return response[key];
    }
  }

  return [];
}

function debugResponse(label, response) {
  if (!response) { console.warn(`[${label}] response is null/undefined`); return; }
  const keys = typeof response === 'object' ? Object.keys(response) : typeof response;
  console.log(`[${label}] top-level keys:`, keys);
  if (typeof response === 'object') {
    for (const k of Object.keys(response)) {
      if (Array.isArray(response[k]) && response[k].length > 0) {
        console.log(`[${label}] ${k}[0]:`, response[k][0]);
      }
    }
  }
}

// ── Booking Plans ─────────────────────────────────────────

export async function getBookingPlans(payload = {}) {
  const {
    search, status, date_from, date_to, doctor_id, room_id,
    main, ipopnumber, patient_name, doctor_name, booking_time,
    room_number, booking_date, column_status, visit_type, treatment_type,
    offset = 0, limit = 7, sort_order, sort_column,
  } = payload;
  const params = new URLSearchParams();
  if (search)    params.append("search", search);
  if (status)    params.append("status", status);
  if (date_from) params.append("date_from", date_from);
  if (date_to)   params.append("date_to", date_to);
  if (doctor_id) params.append("doctor_id", doctor_id);
  if (room_id)   params.append("room_id", room_id);
  if (main) params.append("main", main);
  if (ipopnumber) params.append("ipopnumber", ipopnumber);
  if (patient_name) params.append("patient_name", patient_name);
  if (doctor_name) params.append("doctor_name", doctor_name);
  if (booking_time) params.append("booking_time", booking_time);
  if (room_number) params.append("room_number", room_number);
  if (booking_date) params.append("booking_date", booking_date);
  if (column_status) params.append("column_status", column_status);
  if (visit_type) params.append("visit_type", visit_type);
  if (treatment_type) params.append("treatment_type", treatment_type);
  if (sort_order) params.append("sort_order", sort_order);
  if (sort_column) params.append("sort_column", sort_column);
  params.append("offset", String(offset));
  params.append("limit", String(limit));
  return apiFetch(`${basePath}/booking-plans?${params.toString()}`, {
    method: "GET", headers: buildHeaders(),
  });
}

export async function getBookingPlanById(id) {
  return apiFetch(`${basePath}/booking-plans/${id}`, {
    method: "GET", headers: buildHeaders(),
  });
}

export async function getBookingPlanByPatientId(patientId, assignDoctorOnly = false, visitId = null, boardableToday = false) {
  const params = new URLSearchParams();
  if (assignDoctorOnly) params.append("assignDoctorOnly", "true");
  if (visitId) params.append("visitId", String(visitId));
  if (boardableToday) params.append("boardableToday", "true");
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`${basePath}/booking-plans/patient/${patientId}${query}`, {
    method: "GET", headers: buildHeaders(),
  });
}

export async function validateBookingPlan(planData) {
  return apiFetch(`${basePath}/booking-plans/validate`, {
    method: "POST", headers: buildHeaders(), body: JSON.stringify(planData),
  });
}

export async function createBookingPlan(planData) {
  return apiFetch(`${basePath}/booking-plans`, {
    method: "POST", headers: buildHeaders(), body: JSON.stringify(planData),
  });
}

export async function updateBookingPlan(id, planData) {
  return apiFetch(`${basePath}/booking-plans/${id}`, {
    method: "PUT", headers: buildHeaders(), body: JSON.stringify(planData),
  });
}

export async function updateBookingPlanStatus(id, status) {
  return apiFetch(`${basePath}/booking-plans/${id}/status`, {
    method: "PUT", headers: buildHeaders(), body: JSON.stringify({ status }),
  });
}

export async function updateBookingPlanStatusWithComments(id, status, comments) {
  return apiFetch(`${basePath}/booking-plans/${id}/status`, {
    method: "PUT", headers: buildHeaders(), body: JSON.stringify({ status, comments }),
  });
}

export async function cancelBookingPlan(id) {
  return updateBookingPlanStatus(id, 'Cancelled');
}

export async function completeBookingPlan(id) {
  return updateBookingPlanStatus(id, 'Completed');
}

export async function deleteBookingPlan(id) {
  return apiFetch(`${basePath}/booking-plans/${id}`, {
    method: "DELETE", headers: buildHeaders(),
  });
}

export async function hardDeleteBookingPlan(id) {
  return apiFetch(`${basePath}/booking-plans/${id}/hard`, {
    method: "DELETE", headers: buildHeaders(),
  });
}

// ── PATIENT BOARDING ──────────────────────────────────────

/**
 * Board a patient: creates a visit and stores visit_id in booking_plan.
 * POST /api/booking-plans/{id}/board
 */
export async function boardPatient(bookingId, boardingPayload = {}) {
  return apiFetch(`${basePath}/booking-plans/${bookingId}/board`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(boardingPayload),
  });
}

/** Start a boarded booking session. */
export async function startBookingSession(bookingId) {
  return apiFetch(`${basePath}/booking-plans/${bookingId}/start`, {
    method: "POST", headers: buildHeaders(),
  });
}

/**
 * Mark a booking as completed (only when visit_id exists).
 * POST /api/booking-plans/{id}/complete
 */
export async function markBookingComplete(bookingId) {
  return apiFetch(`${basePath}/booking-plans/${bookingId}/complete`, {
    method: "POST", headers: buildHeaders(),
  });
}

// ── Patients ──────────────────────────────────────────────

export async function getPatients() {
  const response = await apiFetch(`${basePath}/v1/allpatients`, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getPatients", response);
  return { data: extractList(response) };
}

export async function getClosedVisitPatients(search = "") {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  const response = await apiFetch(`${basePath}/booking-plans/closed-visits?${params.toString()}`, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getClosedVisitPatients", response);
  return { data: extractList(response) };
}

export async function getOpenVisitPatients(search = "") {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  const response = await apiFetch(`${basePath}/booking-plans/open-visits?${params.toString()}`, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getOpenVisitPatients", response);
  return { data: extractList(response) };
}

// ── Doctors ───────────────────────────────────────────────

export async function getDoctors() {
  const response = await apiFetch(`${basePath}/gv-doctors`, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getDoctors", response);
  return { data: extractList(response) };
}

// ── Rooms ─────────────────────────────────────────────────

export async function getRooms(searchText = "") {
  const url = `${basePath}/v1/rooms?facilityId=&roomStatus=`;
  const response = await apiFetch(url, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getRooms", response);
  let rooms = extractList(response);
  if (searchText) {
    const term = searchText.toLowerCase();
    rooms = rooms.filter((room) =>
      [room?.roomName, room?.roomCode, room?.description]
        .some((value) => String(value || "").toLowerCase().includes(term))
    );
  }
  return { data: rooms };
}

// ── Packages ──────────────────────────────────────────────

export async function getPackages() {
  const response = await apiFetch(`${basePath}/v1/package`, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getPackages", response);
  const pkgData = response?.packageList || extractList(response);
  console.log('[bookingPlanService] getPackages extracted:', pkgData?.length ?? 0, 'items');
  return { data: pkgData };
}

// ── Package Services ──────────────────────────────────────

export async function getPackageServices(packageId = null) {
  const url = packageId
    ? `${basePath}/v1/packageService?visitId=${packageId}`
    : `${basePath}/v1/packageService`;
  const response = await apiFetch(url, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("getPackageServices", response);
  const data = response?.packageServiceList || extractList(response);
  return { data };
}

// ── Package Service Search (dedicated search API) ─────────
export async function searchPackageServices(searchTerm) {
  const url = `${basePath}/v1/packageService?searchTerm=${encodeURIComponent(searchTerm)}`;
  const response = await apiFetch(url, {
    method: "GET", headers: buildHeaders(),
  });
  debugResponse("searchPackageServices", response);
  const data = response?.packageServiceList || extractList(response);
  return { data };
}

// ── Doctor Schedule ───────────────────────────────────────

export async function getDoctorSchedule(doctorId, date) {
  return apiFetch(`${basePath}/doctors/${doctorId}/schedule?date=${date}`, {
    method: "GET", headers: buildHeaders(),
  });
}

// ── Single-record lookups ─────────────────────────────────

export async function getPatientById(id) {
  return apiFetch(`${basePath}/v1/patient?patientId=${id}`, {
    method: "GET", headers: buildHeaders(),
  });
}

export async function getDoctorById(id) {
  return apiFetch(`${basePath}/doctors/${id}`, {
    method: "GET", headers: buildHeaders(),
  });
}

// ── Named export ──────────────────────────────────────────

/**
 * Update an existing open visit with doctor assignment details.
 * Sets apptdate, appthours, apptminutes, clears capptdate/cappthours,
 * sets department=0 and departmentId to doctor's department.
 */
export async function updateVisitWithDoctorAssignment(visitData) {
  return apiFetch(`${basePath}/v1/visit/doctor-assignment`, {
    method: "PUT", headers: buildHeaders(), body: JSON.stringify(visitData),
  });
}

/**
 * Creates a booking plan linked to an existing open visit.
 * Updates the visit record AND creates a booking_plan with visit_id + ipopnumber.
 * Used by SearchDocList when assigning doctor to patient with open visit.
 */
export async function createBookingPlanForExistingVisit(bookingData) {
  return apiFetch(`${basePath}/booking-plans/existing-visit`, {
    method: "POST", headers: buildHeaders(), body: JSON.stringify(bookingData),
  });
}

export const bookingPlanService = {
  getBookingPlans,
  getBookingPlanById,
  getBookingPlanByPatientId,
  createBookingPlan,
  createBookingPlanForExistingVisit,
  updateBookingPlan,
  updateBookingPlanStatus,
  updateBookingPlanStatusWithComments,
  cancelBookingPlan,
  completeBookingPlan,
  deleteBookingPlan,
  hardDeleteBookingPlan,
  boardPatient,
  startBookingSession,
  markBookingComplete,
  getPatients,
  getClosedVisitPatients,
  getOpenVisitPatients,
  getDoctors,
  getRooms,
  getPackages,
  getPackageServices,
  searchPackageServices,
  getDoctorSchedule,
  getPatientById,
  getDoctorById,
  updateVisitWithDoctorAssignment,
};

export default bookingPlanService;
